/**
 * Build NES PPU state (pattern table + nametable + attribute + bg subpalettes)
 * from an arbitrary RGBA image. The image is cropped/scaled to 256×240, each
 * 8×8 cell is quantized down to a 4-color subpalette (chosen per 16×16
 * attribute block), and tiles are deduplicated up to 256.
 *
 * This is a "reasonable approximation". Color picking is heuristic — k-means
 * over per-block dominant palettes — and tile dedup is exact-match. The point
 * of this tool is to feed the glitch system, not to reproduce a real NES
 * conversion.
 */

import {
    NAMETABLE_TILES_X,
    NAMETABLE_TILES_Y,
    NAMETABLE_TILES,
    ATTRIBUTE_TABLE_BYTES,
    PATTERN_TABLE_BYTES,
    NES_SCREEN_WIDTH,
    NES_SCREEN_HEIGHT,
} from "./nesPpu";
import { nearestNesIndex, getNesMasterRgb } from "./nesPalette";

/** How to derive the four BG subpalettes from an imported 256×240 frame. */
export type ImagePaletteStrategy =
    /** Per-16×16 top colors → k-means into 4 subpalettes (original behavior). */
    | "block-kmeans"
    /** One global top-3 (after universal bg) shared by all four subpalettes. */
    | "global-histogram"
    /** Greedily pick 12 well-separated master colors (by RGB distance), split into four triplets. */
    | "wide-spread"
    /** Each subpalette from a screen quadrant (128×120): that region’s top 3 colors + universal bg. */
    | "screen-quarters"
    /** Sparse grid sample → histogram → four consecutive triplets from the top-12 sample colors. */
    | "grid-medoid";

export const IMAGE_PALETTE_STRATEGIES: {
    id: ImagePaletteStrategy;
    label: string;
    description: string;
}[] = [
    {
        id: "block-kmeans",
        label: "Block k-means",
        description:
            "Each 16×16 block votes its top-4 colors; k-means merges blocks into 4 subpalettes.",
    },
    {
        id: "global-histogram",
        label: "Global histogram",
        description:
            "Universal bg = whole-frame mode; the three next most common NES colors fill every BG subpalette.",
    },
    {
        id: "wide-spread",
        label: "Wide spread",
        description:
            "Picks 12 frequent colors that stay far apart in RGB, then uses three per subpalette for a posterized look.",
    },
    {
        id: "screen-quarters",
        label: "Screen quarters",
        description:
            "Each of the four 128×120 quadrants contributes its own top-3 (plus shared universal bg).",
    },
    {
        id: "grid-medoid",
        label: "Grid medoid",
        description:
            "Histogram only on a coarse pixel grid, then slices the top-12 sample colors into four triplets.",
    },
];

/** Post-process imported BG palettes / attribute assignment for a calmer look. */
export type ImageColorConsistency =
    | "none"
    | "spatial-smoothing"
    | "shared-subpalette";

export const IMAGE_COLOR_CONSISTENCY_OPTIONS: {
    id: ImageColorConsistency;
    label: string;
    description: string;
}[] = [
    {
        id: "none",
        label: "None",
        description:
            "Each 16×16 block picks the best-matching subpalette independently (default).",
    },
    {
        id: "spatial-smoothing",
        label: "Spatial smoothing",
        description:
            "Favor the same subpalette as neighboring blocks when fit is close, reducing noisy attribute boundaries.",
    },
    {
        id: "shared-subpalette",
        label: "Shared subpalette colors",
        description:
            "After building four subpalettes, force the first two accent colors to match the image-wide top colors; each subpalette keeps a distinct third accent.",
    },
];

export function isValidImageColorConsistency(
    s: string,
): s is ImageColorConsistency {
    return IMAGE_COLOR_CONSISTENCY_OPTIONS.some((o) => o.id === s);
}

export interface ImageImportOptions {
    strategy?: ImagePaletteStrategy;
    colorConsistency?: ImageColorConsistency;
}

/** How the source bitmap is mapped onto the fixed 256×240 frame before quantization. */
export type ImageFrameFit =
    | "cover-center"
    | "cover-top"
    | "cover-bottom"
    | "cover-left"
    | "cover-right"
    | "contain"
    | "stretch";

export const IMAGE_FRAME_FIT_OPTIONS: {
    id: ImageFrameFit;
    label: string;
    description: string;
}[] = [
    {
        id: "cover-center",
        label: "Cover · center",
        description:
            "Crop to 256:240 aspect, centered (default). Pixels are crisp-scaled.",
    },
    {
        id: "cover-top",
        label: "Cover · top",
        description:
            "When cropping vertically, align to the top edge; wide images stay horizontally centered.",
    },
    {
        id: "cover-bottom",
        label: "Cover · bottom",
        description:
            "Vertical crop aligned to the bottom; wide images stay centered horizontally.",
    },
    {
        id: "cover-left",
        label: "Cover · left",
        description:
            "When cropping horizontally, align to the left edge; tall images stay vertically centered.",
    },
    {
        id: "cover-right",
        label: "Cover · right",
        description:
            "Horizontal crop aligned to the right; tall images stay vertically centered.",
    },
    {
        id: "contain",
        label: "Contain",
        description:
            "Fit the entire image inside 256×240 with letterboxing (black bars). Import zoom is ignored.",
    },
    {
        id: "stretch",
        label: "Stretch",
        description:
            "Distort the image to fill 256×240. Import zoom shrinks/expands the sampled source window before stretching.",
    },
];

export interface LoadImageToFrameOptions {
    fit?: ImageFrameFit;
    /**
     * 1 = default. Values > 1 zoom in (smaller source rectangle before scaling).
     * Values < 1 zoom out (larger rectangle, clamped to the bitmap). Applies to
     * cover-* and stretch; ignored for contain.
     */
    zoom?: number;
}

export const IMAGE_IMPORT_ZOOM_MIN = 0.25;
export const IMAGE_IMPORT_ZOOM_MAX = 4;

export function clampImageImportZoom(n: number): number {
    if (!Number.isFinite(n)) return 1;
    return Math.max(IMAGE_IMPORT_ZOOM_MIN, Math.min(IMAGE_IMPORT_ZOOM_MAX, n));
}

export function isValidImageFrameFit(s: string): s is ImageFrameFit {
    return IMAGE_FRAME_FIT_OPTIONS.some((o) => o.id === s);
}

export interface ImageImportResult {
    patternTable: Uint8Array;
    /** A duplicate (or an empty buffer) is returned for the second slot. */
    altPatternTable: Uint8Array;
    nametable: Uint8Array;
    attribute: Uint8Array;
    /** 32-byte palette RAM with bg portion derived from the image. */
    palette: Uint8Array;
    /** True if any tiles had to be dropped because we ran out of pattern slots. */
    overflow: boolean;
    /** Number of unique tiles used (1..256). */
    uniqueTiles: number;
}

/**
 * Load an image File / Blob to a 256×240 ImageData (cover / contain / stretch + optional zoom).
 */
export async function loadImageToFrameImageData(
    blob: Blob,
    options?: LoadImageToFrameOptions,
): Promise<ImageData> {
    const fit: ImageFrameFit = options?.fit ?? "cover-center";
    const zoom = clampImageImportZoom(options?.zoom ?? 1);
    const url = URL.createObjectURL(blob);
    try {
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const im = new Image();
            im.onload = () => resolve(im);
            im.onerror = () => reject(new Error("Failed to load image"));
            im.src = url;
        });
        const iw = img.naturalWidth || img.width;
        const ih = img.naturalHeight || img.height;
        if (iw <= 0 || ih <= 0) {
            throw new Error("Invalid image dimensions");
        }
        const canvas = document.createElement("canvas");
        canvas.width = NES_SCREEN_WIDTH;
        canvas.height = NES_SCREEN_HEIGHT;
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("Canvas 2D not available");
        ctx.imageSmoothingEnabled = false;

        if (fit === "contain") {
            const scale = Math.min(
                NES_SCREEN_WIDTH / iw,
                NES_SCREEN_HEIGHT / ih,
            );
            const dw = iw * scale;
            const dh = ih * scale;
            const ox = (NES_SCREEN_WIDTH - dw) / 2;
            const oy = (NES_SCREEN_HEIGHT - dh) / 2;
            ctx.fillStyle = "#000000";
            ctx.fillRect(0, 0, NES_SCREEN_WIDTH, NES_SCREEN_HEIGHT);
            ctx.drawImage(img, 0, 0, iw, ih, ox, oy, dw, dh);
        } else if (fit === "stretch") {
            const r = applyZoomToSourceRect(0, 0, iw, ih, iw, ih, zoom);
            ctx.drawImage(
                img,
                r.sx,
                r.sy,
                r.sw,
                r.sh,
                0,
                0,
                NES_SCREEN_WIDTH,
                NES_SCREEN_HEIGHT,
            );
        } else {
            const r = computeCoverSourceRect(iw, ih, fit, zoom);
            ctx.drawImage(
                img,
                r.sx,
                r.sy,
                r.sw,
                r.sh,
                0,
                0,
                NES_SCREEN_WIDTH,
                NES_SCREEN_HEIGHT,
            );
        }
        return ctx.getImageData(0, 0, NES_SCREEN_WIDTH, NES_SCREEN_HEIGHT);
    } finally {
        URL.revokeObjectURL(url);
    }
}

function applyZoomToSourceRect(
    sx: number,
    sy: number,
    sw: number,
    sh: number,
    iw: number,
    ih: number,
    zoom: number,
): { sx: number; sy: number; sw: number; sh: number } {
    const z = clampImageImportZoom(zoom);
    const cx = sx + sw * 0.5;
    const cy = sy + sh * 0.5;
    let nw = sw / z;
    let nh = sh / z;
    let nx = cx - nw * 0.5;
    let ny = cy - nh * 0.5;
    nx = Math.max(0, Math.min(nx, iw - nw));
    ny = Math.max(0, Math.min(ny, ih - nh));
    if (nw > iw) {
        nw = iw;
        nx = 0;
    }
    if (nh > ih) {
        nh = ih;
        ny = 0;
    }
    return { sx: nx, sy: ny, sw: nw, sh: nh };
}

type CoverFrameFit = Exclude<ImageFrameFit, "contain" | "stretch">;

function computeCoverSourceRect(
    iw: number,
    ih: number,
    fit: CoverFrameFit,
    zoom: number,
): { sx: number; sy: number; sw: number; sh: number } {
    const dstAR = NES_SCREEN_WIDTH / NES_SCREEN_HEIGHT;
    const srcAR = iw / ih;
    let sx = 0;
    let sy = 0;
    let sw = iw;
    let sh = ih;
    if (srcAR > dstAR) {
        sh = ih;
        sw = ih * dstAR;
        if (fit === "cover-left") sx = 0;
        else if (fit === "cover-right") sx = iw - sw;
        else sx = (iw - sw) / 2;
        sy = 0;
    } else {
        sw = iw;
        sh = iw / dstAR;
        sx = 0;
        if (fit === "cover-top") sy = 0;
        else if (fit === "cover-bottom") sy = ih - sh;
        else sy = (ih - sh) / 2;
    }
    return applyZoomToSourceRect(sx, sy, sw, sh, iw, ih, zoom);
}

type Quad = [number, number, number, number];

/**
 * Convert ImageData → NES PPU bg state. Pipeline:
 *  1. Quantize every pixel to a master-palette index.
 *  2. Per 16×16 block, build a 4-color candidate quad (histogram).
 *  3. Derive four BG subpalettes using `options.strategy` (default: block k-means).
 *  4. Assign each block to the nearest subpalette, build attribute table.
 *  5. Per 8×8 tile, snap pixels to subpalette + universal bg → CHR + nametable.
 */
export function imageDataToPpuBackground(
    image: ImageData,
    options?: ImageImportOptions,
): ImageImportResult {
    const W = NES_SCREEN_WIDTH;
    const H = NES_SCREEN_HEIGHT;
    if (image.width !== W || image.height !== H) {
        throw new Error("imageDataToPpuBackground: expected 256×240 ImageData");
    }

    const strategy: ImagePaletteStrategy = options?.strategy ?? "block-kmeans";
    const colorConsistency: ImageColorConsistency =
        options?.colorConsistency ?? "none";

    const data = image.data;
    const masterRgb = getNesMasterRgb();

    // (1) Quantize each pixel to a master-palette index (0..63).
    const quantized = new Uint8Array(W * H);
    for (let i = 0, p = 0; i < data.length; i += 4, p++) {
        quantized[p] = nearestNesIndex(data[i], data[i + 1], data[i + 2]);
    }

    const ATTR_X = NAMETABLE_TILES_X / 2;
    const ATTR_Y = NAMETABLE_TILES_Y / 2;
    const blockPalettes = buildBlockPalettes(quantized, W, H, ATTR_X, ATTR_Y);
    const universalBg = computeUniversalBg(quantized);
    let subpalettes = computeSubpalettesForStrategy(
        strategy,
        quantized,
        blockPalettes,
        masterRgb,
        universalBg,
        W,
        H,
    );
    if (colorConsistency === "shared-subpalette") {
        subpalettes = deriveSharedSubpalettes(
            subpalettes,
            universalBg,
            quantized,
            masterRgb,
        );
    }
    let blockSub = assignBlockSubpalettes(
        blockPalettes,
        subpalettes,
        masterRgb,
    );
    if (colorConsistency === "spatial-smoothing") {
        blockSub = spatialSmoothBlockSub(
            blockSub,
            blockPalettes,
            subpalettes,
            ATTR_X,
            ATTR_Y,
            masterRgb,
        );
    }

    // (4) Build attribute table from the per-16×16 subpalette assignment.
    const attribute = new Uint8Array(ATTRIBUTE_TABLE_BYTES);
    for (let ay = 0; ay < 8; ay++) {
        for (let ax = 0; ax < 8; ax++) {
            // Each attribute byte covers 4 quadrants (top-left, top-right, bottom-left, bottom-right)
            // in a 32×32 area — i.e. four 16×16 blocks at (ax*2, ay*2) (+/- 1).
            let byte = 0;
            for (let qy = 0; qy < 2; qy++) {
                for (let qx = 0; qx < 2; qx++) {
                    const bx = ax * 2 + qx;
                    const by = ay * 2 + qy;
                    if (bx >= ATTR_X || by >= ATTR_Y) continue;
                    const sub = blockSub[by * ATTR_X + bx];
                    byte |= (sub & 3) << ((qy * 2 + qx) * 2);
                }
            }
            attribute[ay * 8 + ax] = byte;
        }
    }

    // (5) For each tile, choose the subpalette of its enclosing attribute
    //     block, then quantize the 8×8 tile into 2-bit values.
    const patternTable = new Uint8Array(PATTERN_TABLE_BYTES);
    const altPatternTable = new Uint8Array(PATTERN_TABLE_BYTES);
    const nametable = new Uint8Array(NAMETABLE_TILES);
    const tileBytesByKey = new Map<string, number>(); // key → tileIndex
    let tileCount = 0;
    let overflow = false;
    const droppedTiles: Uint8Array[] = [];

    for (let ty = 0; ty < NAMETABLE_TILES_Y; ty++) {
        for (let tx = 0; tx < NAMETABLE_TILES_X; tx++) {
            const blockIdx = Math.floor(ty / 2) * ATTR_X + Math.floor(tx / 2);
            const sub = blockSub[blockIdx];
            const subPal = subpalettes[sub];
            // Convert tile pixels to 2-bit values.
            const tileBytes = new Uint8Array(16);
            for (let py = 0; py < 8; py++) {
                let lo = 0;
                let hi = 0;
                for (let px = 0; px < 8; px++) {
                    const sourceIdx = (ty * 8 + py) * W + (tx * 8 + px);
                    const masterIdx = quantized[sourceIdx];
                    const value = chooseTilePixelValue(
                        masterIdx,
                        universalBg,
                        subPal,
                        masterRgb,
                    );
                    if (value & 1) lo |= 1 << (7 - px);
                    if (value & 2) hi |= 1 << (7 - px);
                }
                tileBytes[py] = lo;
                tileBytes[8 + py] = hi;
            }
            const key = tileKey(tileBytes);
            let tileIndex = tileBytesByKey.get(key);
            if (tileIndex === undefined) {
                if (tileCount < 256) {
                    tileIndex = tileCount;
                    tileBytesByKey.set(key, tileIndex);
                    patternTable.set(tileBytes, tileIndex * 16);
                    tileCount++;
                } else {
                    overflow = true;
                    droppedTiles.push(tileBytes);
                    tileIndex = nearestExistingTile(
                        tileBytes,
                        patternTable,
                        tileCount,
                    );
                }
            }
            nametable[ty * NAMETABLE_TILES_X + tx] = tileIndex;
        }
    }

    // Stash a few dropped tiles into the alt pattern table so users can swap
    // banks and see them. Anything past 256 is unfortunately gone.
    for (let i = 0; i < Math.min(droppedTiles.length, 256); i++) {
        altPatternTable.set(droppedTiles[i], i * 16);
    }

    // Build palette RAM. Sprite half is left as a small default rotation so
    // sprites have something visible if injected by glitches.
    const palette = new Uint8Array(32);
    palette[0] = universalBg;
    for (let s = 0; s < 4; s++) {
        for (let c = 0; c < 3; c++) {
            palette[1 + s * 4 + c] = subpalettes[s][c + 1] ?? subpalettes[s][0];
        }
    }
    palette[17] = subpalettes[0][1];
    palette[18] = subpalettes[0][2];
    palette[19] = subpalettes[0][3];
    palette[21] = subpalettes[1][1];
    palette[22] = subpalettes[1][2];
    palette[23] = subpalettes[1][3];
    palette[25] = subpalettes[2][1];
    palette[26] = subpalettes[2][2];
    palette[27] = subpalettes[2][3];
    palette[29] = subpalettes[3][1];
    palette[30] = subpalettes[3][2];
    palette[31] = subpalettes[3][3];

    return {
        patternTable,
        altPatternTable,
        nametable,
        attribute,
        palette,
        overflow,
        uniqueTiles: tileCount,
    };
}

export function isValidImagePaletteStrategy(
    s: string,
): s is ImagePaletteStrategy {
    return IMAGE_PALETTE_STRATEGIES.some((row) => row.id === s);
}

function buildBlockPalettes(
    quantized: Uint8Array,
    W: number,
    H: number,
    attrX: number,
    attrY: number,
): Quad[] {
    const blockPalettes: Quad[] = [];
    for (let by = 0; by < attrY; by++) {
        for (let bx = 0; bx < attrX; bx++) {
            const counts = new Map<number, number>();
            const x0 = bx * 16;
            const y0 = by * 16;
            for (let yy = 0; yy < 16; yy++) {
                for (let xx = 0; xx < 16; xx++) {
                    const idx = quantized[(y0 + yy) * W + (x0 + xx)];
                    counts.set(idx, (counts.get(idx) ?? 0) + 1);
                }
            }
            const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1]);
            const pal: number[] = sorted.slice(0, 4).map((e) => e[0]);
            while (pal.length < 4) pal.push(pal[0] ?? 0x0f);
            blockPalettes.push(pal as Quad);
        }
    }
    return blockPalettes;
}

function computeUniversalBg(quantized: Uint8Array): number {
    const totalCounts = new Map<number, number>();
    for (let i = 0; i < quantized.length; i++) {
        totalCounts.set(quantized[i], (totalCounts.get(quantized[i]) ?? 0) + 1);
    }
    return (
        [...totalCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? 0x0f
    );
}

function assignBlockSubpalettes(
    blockPalettes: Quad[],
    subpalettes: Quad[],
    masterRgb: { r: number; g: number; b: number }[],
): Uint8Array {
    const blockSub = new Uint8Array(blockPalettes.length);
    for (let i = 0; i < blockPalettes.length; i++) {
        const candidate = blockPalettes[i];
        let best = 0;
        let bestDist = Infinity;
        for (let s = 0; s < 4; s++) {
            const d = palDistance(candidate, subpalettes[s], masterRgb);
            if (d < bestDist) {
                bestDist = d;
                best = s;
            }
        }
        blockSub[i] = best;
    }
    return blockSub;
}

/** Penalize differing from 4-neighbors when re-picking subpalette per block. */
const SPATIAL_SMOOTH_LAMBDA = 14_000;
const SPATIAL_SMOOTH_PASSES = 5;

function spatialSmoothBlockSub(
    blockSub: Uint8Array,
    blockPalettes: Quad[],
    subpalettes: Quad[],
    attrX: number,
    attrY: number,
    masterRgb: { r: number; g: number; b: number }[],
): Uint8Array {
    let cur = new Uint8Array(blockSub);
    for (let pass = 0; pass < SPATIAL_SMOOTH_PASSES; pass++) {
        const next = new Uint8Array(cur);
        for (let by = 0; by < attrY; by++) {
            for (let bx = 0; bx < attrX; bx++) {
                const i = by * attrX + bx;
                const cand = blockPalettes[i];
                let bestS = 0;
                let bestCost = Infinity;
                for (let s = 0; s < 4; s++) {
                    const dist = palDistance(cand, subpalettes[s], masterRgb);
                    let disagree = 0;
                    if (bx > 0 && cur[i - 1] !== s) disagree++;
                    if (bx + 1 < attrX && cur[i + 1] !== s) disagree++;
                    if (by > 0 && cur[i - attrX] !== s) disagree++;
                    if (by + 1 < attrY && cur[i + attrX] !== s) disagree++;
                    const cost = dist + disagree * SPATIAL_SMOOTH_LAMBDA;
                    if (cost < bestCost) {
                        bestCost = cost;
                        bestS = s;
                    }
                }
                next[i] = bestS;
            }
        }
        cur = next;
    }
    return cur;
}

/**
 * Two image-wide accent colors (after universal bg) shared by all four
 * subpalettes; each row keeps a third accent from the original strategy row.
 */
function deriveSharedSubpalettes(
    base: Quad[],
    universalBg: number,
    quantized: Uint8Array,
    masterRgb: { r: number; g: number; b: number }[],
): Quad[] {
    const top = sortedMastersByFrequency(quantized, universalBg, 16);
    const g1 = top[0] ?? universalBg;
    let g2 = top[1] ?? g1;
    if (g2 === g1) {
        g2 = top.find((c) => c !== g1) ?? g1;
    }
    const out: Quad[] = [];
    for (let s = 0; s < 4; s++) {
        const o = base[s] ?? [universalBg, g1, g2, g1];
        const x = pickThirdAccent(o, universalBg, g1, g2, top);
        out.push([universalBg, g1, g2, x]);
    }
    return out;
}

function pickThirdAccent(
    o: Quad,
    universalBg: number,
    g1: number,
    g2: number,
    ranked: number[],
): number {
    for (const c of [o[1], o[2], o[3]]) {
        if (c !== g1 && c !== g2) return c;
    }
    for (const c of ranked) {
        if (c !== universalBg && c !== g1 && c !== g2) return c;
    }
    return o[3] ?? g1;
}

function quadFromUbAndTriple(
    ub: number,
    a: number,
    b: number,
    c: number,
): Quad {
    return [ub, a, b, c];
}

function computeSubpalettesForStrategy(
    strategy: ImagePaletteStrategy,
    quantized: Uint8Array,
    blockPalettes: Quad[],
    masterRgb: { r: number; g: number; b: number }[],
    universalBg: number,
    W: number,
    H: number,
): Quad[] {
    switch (strategy) {
        case "block-kmeans":
            return clusterBlockPalettes(blockPalettes, masterRgb, 4);
        case "global-histogram": {
            const sorted = sortedMastersByFrequency(quantized, universalBg, 3);
            const a = sorted[0] ?? universalBg;
            const b = sorted[1] ?? a;
            const c = sorted[2] ?? b;
            const q = quadFromUbAndTriple(universalBg, a, b, c);
            return [q, q, q, q];
        }
        case "wide-spread": {
            const chosen = greedyMaxMinSpread(
                quantized,
                masterRgb,
                universalBg,
                12,
            );
            const out: Quad[] = [];
            for (let s = 0; s < 4; s++) {
                const a = chosen[s * 3] ?? chosen[0] ?? universalBg;
                const b = chosen[s * 3 + 1] ?? a;
                const c = chosen[s * 3 + 2] ?? b;
                out.push(quadFromUbAndTriple(universalBg, a, b, c));
            }
            return out;
        }
        case "screen-quarters": {
            const halfW = Math.floor(W / 2);
            const halfH = Math.floor(H / 2);
            const rects: [number, number, number, number][] = [
                [0, 0, halfW - 1, halfH - 1],
                [halfW, 0, W - 1, halfH - 1],
                [0, halfH, halfW - 1, H - 1],
                [halfW, halfH, W - 1, H - 1],
            ];
            return rects.map(([x0, y0, x1, y1]) => {
                const tri = topDistinctInRect(
                    quantized,
                    W,
                    x0,
                    y0,
                    x1,
                    y1,
                    universalBg,
                    3,
                );
                return quadFromUbAndTriple(universalBg, tri[0], tri[1], tri[2]);
            });
        }
        case "grid-medoid": {
            const sample: number[] = [];
            const step = 6;
            for (let y = 0; y < H; y += step) {
                for (let x = 0; x < W; x += step) {
                    sample.push(quantized[y * W + x]);
                }
            }
            const hist = new Map<number, number>();
            for (const v of sample) {
                hist.set(v, (hist.get(v) ?? 0) + 1);
            }
            const ranked = [...hist.entries()]
                .sort((a, b) => b[1] - a[1])
                .map((e) => e[0]);
            const top12: number[] = [];
            for (let i = 0; i < ranked.length && top12.length < 12; i++) {
                top12.push(ranked[i]);
            }
            while (top12.length < 12) {
                top12.push(top12[top12.length - 1] ?? universalBg);
            }
            const out: Quad[] = [];
            for (let s = 0; s < 4; s++) {
                const a = top12[s * 3];
                const b = top12[s * 3 + 1];
                const c = top12[s * 3 + 2];
                out.push(quadFromUbAndTriple(universalBg, a, b, c));
            }
            return out;
        }
        default:
            return clusterBlockPalettes(blockPalettes, masterRgb, 4);
    }
}

/** Most frequent master indices, optionally skipping `universalBg`, up to `count`. */
function sortedMastersByFrequency(
    quantized: Uint8Array,
    skip: number | null,
    count: number,
): number[] {
    const hist = new Map<number, number>();
    for (let i = 0; i < quantized.length; i++) {
        const v = quantized[i];
        if (skip !== null && v === skip) continue;
        hist.set(v, (hist.get(v) ?? 0) + 1);
    }
    const sorted = [...hist.entries()]
        .sort((a, b) => b[1] - a[1])
        .map((e) => e[0]);
    const out: number[] = [];
    for (const c of sorted) {
        out.push(c);
        if (out.length >= count) break;
    }
    return out;
}

function topDistinctInRect(
    quantized: Uint8Array,
    W: number,
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    exclude: number,
    k: number,
): number[] {
    const counts = new Map<number, number>();
    for (let y = y0; y <= y1; y++) {
        for (let x = x0; x <= x1; x++) {
            const idx = quantized[y * W + x];
            counts.set(idx, (counts.get(idx) ?? 0) + 1);
        }
    }
    const sorted = [...counts.entries()]
        .sort((a, b) => b[1] - a[1])
        .map((e) => e[0]);
    const out: number[] = [];
    for (const c of sorted) {
        if (c === exclude) continue;
        out.push(c);
        if (out.length >= k) break;
    }
    while (out.length < k) {
        out.push(out[out.length - 1] ?? exclude);
    }
    return out;
}

function rgbDistSq(
    rgb: { r: number; g: number; b: number }[],
    i: number,
    j: number,
): number {
    const a = rgb[i];
    const b = rgb[j];
    const dr = a.r - b.r;
    const dg = a.g - b.g;
    const db = a.b - b.b;
    return dr * dr + dg * dg + db * db;
}

function minDistToSet(
    rgb: { r: number; g: number; b: number }[],
    candidate: number,
    set: number[],
): number {
    let m = Infinity;
    for (const s of set) {
        const d = rgbDistSq(rgb, candidate, s);
        if (d < m) m = d;
    }
    return m;
}

/**
 * Greedily pick up to `want` master indices from frequent colors in `quantized`,
 * maximizing the minimum RGB distance to `universalBg` and to all already chosen.
 */
function greedyMaxMinSpread(
    quantized: Uint8Array,
    rgb: { r: number; g: number; b: number }[],
    universalBg: number,
    want: number,
): number[] {
    const hist = new Map<number, number>();
    for (let i = 0; i < quantized.length; i++) {
        const v = quantized[i];
        hist.set(v, (hist.get(v) ?? 0) + 1);
    }
    const pool = [...hist.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, 48)
        .map((e) => e[0])
        .filter((c) => c !== universalBg);
    const chosen: number[] = [];
    const refSet = (): number[] => [universalBg, ...chosen];
    while (chosen.length < want && pool.length > 0) {
        let bestIdx = -1;
        let bestScore = -1;
        for (let pi = 0; pi < pool.length; pi++) {
            const cand = pool[pi];
            const md = minDistToSet(rgb, cand, refSet());
            if (md > bestScore) {
                bestScore = md;
                bestIdx = pi;
            }
        }
        if (bestIdx < 0 || bestScore < 0) break;
        chosen.push(pool.splice(bestIdx, 1)[0]);
    }
    while (chosen.length < want) {
        chosen.push(chosen[chosen.length - 1] ?? universalBg);
    }
    return chosen;
}

function chooseTilePixelValue(
    masterIdx: number,
    universalBg: number,
    subPal: number[],
    rgb: { r: number; g: number; b: number }[],
): number {
    // Value 0 always means "use universal background", so prefer it whenever
    // the pixel is closer to the universal bg than to any of the 3 subpalette
    // entries 1..3.
    const candidates: { value: number; idx: number }[] = [
        { value: 0, idx: universalBg },
        { value: 1, idx: subPal[1] },
        { value: 2, idx: subPal[2] },
        { value: 3, idx: subPal[3] },
    ];
    const target = rgb[masterIdx];
    let bestValue = 0;
    let bestDist = Infinity;
    for (const { value, idx } of candidates) {
        const c = rgb[idx];
        const dr = c.r - target.r;
        const dg = c.g - target.g;
        const db = c.b - target.b;
        const d = dr * dr + dg * dg + db * db;
        if (d < bestDist) {
            bestDist = d;
            bestValue = value;
        }
    }
    return bestValue;
}

function tileKey(tile: Uint8Array): string {
    let s = "";
    for (let i = 0; i < tile.length; i++) {
        s += tile[i].toString(16).padStart(2, "0");
    }
    return s;
}

function nearestExistingTile(
    tile: Uint8Array,
    table: Uint8Array,
    count: number,
): number {
    let best = 0;
    let bestDist = Infinity;
    for (let t = 0; t < count; t++) {
        let d = 0;
        const off = t * 16;
        for (let i = 0; i < 16; i++) {
            // popcount of XOR (Hamming distance over bitplanes)
            const x = tile[i] ^ table[off + i];
            d += popcount8(x);
        }
        if (d < bestDist) {
            bestDist = d;
            best = t;
        }
    }
    return best;
}

function popcount8(x: number): number {
    let n = x & 0xff;
    n = n - ((n >> 1) & 0x55);
    n = (n & 0x33) + ((n >> 2) & 0x33);
    return (n + (n >> 4)) & 0x0f;
}

/**
 * Cluster `blockPalettes` (each is a 4-color quad of master-palette indices)
 * into `k` global subpalettes by Lloyd-style iteration on the concatenated
 * 12-D RGB vector.
 */
function clusterBlockPalettes(
    blocks: [number, number, number, number][],
    rgb: { r: number; g: number; b: number }[],
    k: number,
): [number, number, number, number][] {
    if (blocks.length === 0) {
        const fallback: [number, number, number, number] = [
            0x0f, 0x00, 0x10, 0x20,
        ];
        return Array.from({ length: k }, () => fallback) as [
            number,
            number,
            number,
            number,
        ][];
    }
    // Init: pick `k` evenly spaced blocks.
    const centers: [number, number, number, number][] = [];
    for (let i = 0; i < k; i++) {
        const idx = Math.floor((i * blocks.length) / k);
        centers.push([...blocks[idx]] as [number, number, number, number]);
    }

    for (let iter = 0; iter < 6; iter++) {
        const groups: [number, number, number, number][][] = Array.from(
            { length: k },
            () => [],
        );
        for (const block of blocks) {
            let best = 0;
            let bestD = Infinity;
            for (let c = 0; c < k; c++) {
                const d = palDistance(block, centers[c], rgb);
                if (d < bestD) {
                    bestD = d;
                    best = c;
                }
            }
            groups[best].push(block);
        }
        for (let c = 0; c < k; c++) {
            if (groups[c].length === 0) continue;
            centers[c] = averagePalette(groups[c], rgb);
        }
    }
    return centers;
}

function palDistance(
    a: [number, number, number, number],
    b: [number, number, number, number],
    rgb: { r: number; g: number; b: number }[],
): number {
    let total = 0;
    for (let i = 0; i < 4; i++) {
        const ca = rgb[a[i]];
        const cb = rgb[b[i]];
        const dr = ca.r - cb.r;
        const dg = ca.g - cb.g;
        const db = ca.b - cb.b;
        total += dr * dr + dg * dg + db * db;
    }
    return total;
}

function averagePalette(
    blocks: [number, number, number, number][],
    rgb: { r: number; g: number; b: number }[],
): [number, number, number, number] {
    const out: [number, number, number, number] = [0, 0, 0, 0];
    for (let i = 0; i < 4; i++) {
        let r = 0;
        let g = 0;
        let b = 0;
        for (const block of blocks) {
            const c = rgb[block[i]];
            r += c.r;
            g += c.g;
            b += c.b;
        }
        const n = blocks.length;
        out[i] = nearestMasterFromAvg(r / n, g / n, b / n, rgb);
    }
    return out;
}

function nearestMasterFromAvg(
    r: number,
    g: number,
    b: number,
    rgb: { r: number; g: number; b: number }[],
): number {
    let best = 0;
    let bestD = Infinity;
    for (let i = 0; i < rgb.length; i++) {
        const lo = i & 0x0f;
        if (lo === 0x0d || lo === 0x0e || lo === 0x0f) continue;
        const c = rgb[i];
        const dr = c.r - r;
        const dg = c.g - g;
        const db = c.b - b;
        const d = dr * dr + dg * dg + db * db;
        if (d < bestD) {
            bestD = d;
            best = i;
        }
    }
    return best;
}
